import { motion } from "framer-motion";
import {
  GraduationCap,
  Truck,
  Droplets,
  ShieldCheck,
  Scissors,
  HeartHandshake,
  CreditCard,
  Receipt,
  Star,
  Headphones,
  ArrowRight,
  Sparkles
} from "lucide-react";

const FEATURES = [
  {
    id: "why-1",
    icon: GraduationCap,
    title: "Professional & Certified Groomers",
    desc: "Background-checked, certified experts trained in gentle pet handling and breed-specific techniques.",
    badgeColor: "bg-amber-500/15 text-amber-600 border-amber-200",
    iconColor: "text-amber-500",
    tagline: "✓ Certified Experts",
    tagColor: "bg-amber-50 text-amber-700 border-amber-200/80"
  },
  {
    id: "why-2",
    icon: Truck,
    title: "Doorstep Grooming at Your Convenience",
    desc: "We bring the complete spa & salon experience directly to your home at your preferred time slot with 0 hassle.",
    badgeColor: "bg-sky-500/15 text-sky-600 border-sky-200",
    iconColor: "text-sky-500",
    tagline: "🏠 At Your Doorstep",
    tagColor: "bg-sky-50 text-sky-700 border-sky-200/80"
  },
  {
    id: "why-3",
    icon: Droplets,
    title: "Premium Pet-Safe Grooming Products",
    desc: "Vet-approved, 100% natural, toxin-free & hypoallergenic grooming shampoos and coat fluffing conditioners.",
    badgeColor: "bg-emerald-500/15 text-emerald-600 border-emerald-200",
    iconColor: "text-emerald-500",
    tagline: "🌿 Vet Approved",
    tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200/80"
  },
  {
    id: "why-4",
    icon: ShieldCheck,
    title: "Hygienic & Sanitized Equipment",
    desc: "Medical-grade sterilization & 100% sanitized equipment before every single pet grooming session.",
    badgeColor: "bg-blue-500/15 text-blue-600 border-blue-200",
    iconColor: "text-blue-500",
    tagline: "✨ 100% Sanitized",
    tagColor: "bg-blue-50 text-blue-700 border-blue-200/80"
  },
  {
    id: "why-5",
    icon: Scissors,
    title: "Personalized Care for Every Breed",
    desc: "Customized grooming techniques & specialized products tailored for your pet's unique coat, skin, and temperament.",
    badgeColor: "bg-rose-500/15 text-rose-600 border-rose-200",
    iconColor: "text-rose-500",
    tagline: "🐾 Breed Tailored",
    tagColor: "bg-rose-50 text-rose-700 border-rose-200/80"
  },
  {
    id: "why-6",
    icon: HeartHandshake,
    title: "Gentle & Stress-Free Grooming Experience",
    desc: "Anxiety-free grooming in familiar home surroundings filled with patience, treats, affection, and gentle care.",
    badgeColor: "bg-orange-500/15 text-orange-600 border-orange-200",
    iconColor: "text-woofsy-primary",
    tagline: "💛 Gentle Care",
    tagColor: "bg-orange-50 text-orange-700 border-orange-200/80"
  },
  {
    id: "why-7",
    icon: CreditCard,
    title: "Easy Online Booking & Secure Payments",
    desc: "Hassle-free 1-click booking system with instant confirmation and 100% secure online payment options.",
    badgeColor: "bg-purple-500/15 text-purple-600 border-purple-200",
    iconColor: "text-purple-500",
    tagline: "🔒 Instant Booking",
    tagColor: "bg-purple-50 text-purple-700 border-purple-200/80"
  },
  {
    id: "why-8",
    icon: Receipt,
    title: "Transparent Pricing with No Hidden Charges",
    desc: "Honest, upfront, fixed pricing with zero hidden salon fees or surprise add-on charges.",
    badgeColor: "bg-teal-500/15 text-teal-600 border-teal-200",
    iconColor: "text-teal-500",
    tagline: "🏷️ Zero Hidden Fees",
    tagColor: "bg-teal-50 text-teal-700 border-teal-200/80"
  },
  {
    id: "why-9",
    icon: Star,
    title: "Trusted by Pet Parents",
    desc: "Over 5,000+ happy pets groomed in Pune with 4.9-star ratings and glowing reviews from pet parents.",
    badgeColor: "bg-yellow-500/15 text-yellow-600 border-yellow-200",
    iconColor: "text-amber-400",
    tagline: "⭐ 5,000+ Happy Pets",
    tagColor: "bg-amber-50 text-amber-700 border-amber-200/80"
  },
  {
    id: "why-10",
    icon: Headphones,
    title: "Dedicated Customer Support",
    desc: "Friendly, prompt customer support team available to assist with bookings, queries, and pet care advice.",
    badgeColor: "bg-cyan-500/15 text-cyan-600 border-cyan-200",
    iconColor: "text-cyan-500",
    tagline: "💬 Dedicated Support",
    tagColor: "bg-cyan-50 text-cyan-700 border-cyan-200/80"
  }
];

export default function WhyWoofsy({ onBook }) {

  return (
    <section id="why" data-testid="why-woofsy" className="relative py-24 sm:py-32 px-5 sm:px-8 overflow-hidden">
      {/* Subtle Ambient Background Lighting */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-woofsy-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs sm:text-sm font-semibold text-woofsy-dark shadow-sm">
            <span className="animate-tail-wag">🐕</span>
            <span>Why Choose Woofsy?</span>
          </div>

          <h2 className="font-heading font-extrabold text-3xl sm:text-5xl text-woofsy-dark tracking-tight leading-tight">
            Why Choose <span className="text-gradient">Woofsy?</span>
          </h2>
          <p className="mt-4 text-lg sm:text-xl font-medium text-woofsy-dark/75">
            Loved by pets. Trusted by pet parents.
          </p>
        </motion.div>

        {/* 10 Feature Cards in Balanced 5-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;

            return (
              <motion.div
                key={f.id}
                data-testid={`why-card-${i}`}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                whileHover={{ y: -6, scale: 1.02 }}
                className="glass rounded-3xl p-5 flex flex-col justify-between border border-white/80 shadow-md hover:shadow-xl transition-all relative group overflow-hidden bg-gradient-to-br from-white/95 via-white/80 to-orange-50/20"
              >
                {/* Subtle Color Accent Bar on Hover */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-woofsy-primary to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />

                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <motion.div
                      whileHover={{ scale: 1.15, rotate: [0, -10, 10, 0] }}
                      transition={{ duration: 0.3 }}
                      className={`shrink-0 w-11 h-11 rounded-2xl ${f.badgeColor} border flex items-center justify-center shadow-sm`}
                    >
                      <Icon className={`w-5 h-5 ${f.iconColor}`} />
                    </motion.div>

                    <span className="text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-slate-100/80 text-slate-500 border border-slate-200/50">
                      #{i + 1}
                    </span>
                  </div>

                  <h3 className="font-heading font-bold text-base text-woofsy-dark mb-2 group-hover:text-woofsy-primary transition-colors leading-snug">
                    {f.title}
                  </h3>

                  <p className="text-woofsy-dark/70 text-xs leading-relaxed">
                    {f.desc}
                  </p>
                </div>

                {/* Bottom Highlight Badge */}
                <div className="mt-4 pt-2.5 flex items-center justify-between border-t border-slate-100/80">
                  <span className="text-[10px] font-semibold text-slate-400">Woofsy</span>

                  <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border shadow-sm ${f.tagColor}`}>
                    {f.tagline}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Full-Width Interactive CTA Banner with Rich Animations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          whileHover={{ scale: 1.01 }}
          className="mt-10 glass rounded-[2rem] p-6 sm:p-8 border-2 border-woofsy-primary/35 shadow-xl bg-gradient-to-r from-woofsy-primary/15 via-orange-500/10 to-amber-400/20 flex flex-wrap items-center justify-between gap-6 relative overflow-hidden group"
        >
          {/* Shimmer Light Beam Sweep Animation across Banner */}
          <div className="shimmer-sweep opacity-30 group-hover:opacity-60 transition-opacity" />

          <div className="flex items-center gap-4 relative z-10">
            {/* Animated Pet Avatar Box with Gentle Float, Pulse Glow & Micro-Badge */}
            <motion.div
              animate={{ y: [0, -6, 0], rotate: [0, -2, 2, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative shrink-0"
            >
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(255,138,61,0.35)] border-2 border-woofsy-primary bg-white p-0.5 transform group-hover:scale-105 transition-transform duration-300">
                <motion.img
                  whileHover={{ scale: 1.15 }}
                  transition={{ duration: 0.3 }}
                  src="https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg?auto=compress&cs=tinysrgb&w=150"
                  alt="Cute puppy avatar"
                  className="w-full h-full object-cover object-center rounded-[14px]"
                />
              </div>

              {/* Floating Sparkle Micro-Badge */}
              <motion.div
                animate={{ scale: [1, 1.25, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-woofsy-primary text-white flex items-center justify-center shadow-md text-xs"
              >
                ✨
              </motion.div>
            </motion.div>

            <div>
              <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-woofsy-dark flex items-center gap-2">
                <span>Ready to Pamper Your Furry Friend?</span>
                <span className="animate-tail-wag inline-block">🐾</span>
              </h3>
              <p className="text-sm text-woofsy-dark/75 mt-0.5">
                Book certified doorstep pet groomers in Pune in under 30 minutes.
              </p>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={onBook}
            className="px-7 py-3.5 rounded-2xl bg-woofsy-primary hover:bg-woofsy-primary/95 text-white font-bold text-sm sm:text-base flex items-center gap-2 shadow-lg shadow-orange-500/30 transition-all shrink-0 relative z-10"
          >
            <span>Book Grooming Now</span>
            <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <ArrowRight className="w-5 h-5" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
